import React, { useState, useMemo, useCallback } from "react";
import {
  SlideoutDrawerV2,
  Flex,
  LoadingIndicator,
  Tabs,
  Tab,
  OverlayStyle,
  toast,
} from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { useBulkClaimTasksMutation, GetWorkflowDocument } from "#graphql/supergraphHooks";
import { ControlTowerContext } from "#utils/context";
import { JIRA_BASE_URL } from "../../constants";
import { useControlTowerContext } from "../../ControlTowerContext";
import { EmptyStateCard } from "../../EmptyStateCard";
import { ClientCommunicationsTab } from "../../WorkflowDetailsDrawer/ClientCommunicationsTab";
import { RequestDetailsTab } from "../../WorkflowDetailsDrawer/RequestDetailsTab";
import { ActionBar } from "../Actions/ActionBar";
import { useDocuments } from "../hooks/useDocuments";
import { useSubtaskActions } from "../hooks/useSubtaskActions";
import { useTaskActions } from "../hooks/useTaskActions";
import { useWorkflowDetail } from "../hooks/useWorkflowDetail";
import { CommentsTab } from "../Tabs/CommentsTab";
import { DocumentsTab } from "../Tabs/DocumentsTab";
import { SubtaskActionsProvider } from "../TaskExecution/SubtaskActionsContext";
import { SubtaskItemType, TaskType } from "../types";
import { isSubtaskValueComplete } from "../utils/subtaskCompletion";
import { MAKER_SUBMISSION_ACTIONS, TASK_CLOSE_ACTIONS } from "./constants";
import { JiraTaskList } from "./JiraTaskList";
import * as styles from "./styles_vanilla.css";
import { TaskDetail } from "./TaskDetail";
import { TaskDetailGPX } from "./TaskDetailGPX";
import { TaskList } from "./TaskList";
import { DetailTabType } from "./types";
import { UnassignedOwnersBanner } from "./UnassignedOwnersBanner";
import { WorkflowHeader } from "./WorkflowHeader";
import type { TaskDocumentPurpose } from "#graphql/supergraphTypes";
import type { WorkflowDetailProps } from "./types";

// Temporary: the Request details tab is only ready for Jira-sourced workflows.
// For in-house workflows it currently shows a "coming soon" placeholder, so we
// hide the tab entirely for now. Flip this to `true` to re-enable it in-house.
const SHOW_IN_APP_REQUEST_DETAILS = false;

export const WorkflowDetail: React.FC<WorkflowDetailProps> = ({
  workflow,
  activeTaskId,
  onClose,
  onTaskIdChange,
  workflowUserId,
}) => {
  const [activeTab, setActiveTab] = useState<DetailTabType>(DetailTabType.Tasks);
  // Pending BTS comment id to scroll to after the Comments tab mounts. Set when
  // a user clicks "See comment" on an activity row; cleared by CommentsTab via
  // onTargetCommentConsumed once the target has been opened and scrolled into view.
  const [targetCommentId, setTargetCommentId] = useState<string | null>(null);
  const [lastWorkflowId, setLastWorkflowId] = useState<number | undefined>(workflow?.id);
  if (workflow?.id !== lastWorkflowId) {
    setLastWorkflowId(workflow?.id);
    setActiveTab(DetailTabType.Tasks);
    setTargetCommentId(null);
  }

  const handleShowComment = (commentId: string): void => {
    setTargetCommentId(commentId);
    setActiveTab(DetailTabType.Communications);
  };

  // Detail/documents/refetch key on the engine workflow id (sourceId), not the
  // unified-row id (workflow.id) which is only the grid's unique row identity.
  const workflowId = workflow ? String(workflow.sourceId) : "";
  const documentsWorkflowId = workflow ? String(workflow.id) : "";
  const isReadOnly = workflow?.source === "JIRA";
  // Portal context decides the external-review (WAITING) button set: the GP
  // portal approves/returns directly, the FA portal records on the GP's behalf.
  const isGPX = useControlTowerContext() === ControlTowerContext.GPX;
  const clientTeamAutoAssignEnabled = useFeatureFlag("controlTowerClientTeamAutoAssign", false);

  const {
    taskSteps: detailedTaskSteps,
    loading: workflowLoading,
    refetch: refetchWorkflow,
    viewerWorkflowUserId: detailViewerWorkflowUserId,
  } = useWorkflowDetail(workflowId, workflow);

  // Prefer the id resolved by this drawer's own GetWorkflow query: it shares
  // task.assignee's id-space and refreshes on every refetch, so the assignee
  // gate self-heals after claim/tab-switch without a hard refresh. Falls back
  // to the table-sourced prop for Jira-sourced workflows (query is skipped).
  const effectiveWorkflowUserId = detailViewerWorkflowUserId ?? workflowUserId;
  const {
    documents,
    stagedDocuments,
    loading: docsLoading,
    isUploadInProgress,
    refetch: refetchDocuments,
    stageDocument,
    deleteUploadedDocument,
    flushPendingAttachments,
    detachObsoleteDocuments,
  } = useDocuments(documentsWorkflowId);
  const { toggleSubtask: _toggleSubtask, updateSubtaskValue: _updateSubtaskValue } =
    useSubtaskActions(isReadOnly ? undefined : workflowId);
  const { performAction, loading: actionLoading } = useTaskActions(
    isReadOnly ? undefined : workflowId,
  );

  const parsedWorkflowId = workflow ? workflow.sourceId : NaN;

  // CRLT-640: bulk "Assign to me" button in the Tasks header. Kept intact so
  // the pre-CRLT-766 UX survives when the client-team auto-assign flag is off.
  // When the flag is ON, UnassignedOwnersBanner supersedes this and we suppress
  // the button (see onBulkAssignToMe wiring below).
  const [bulkClaimTasks] = useBulkClaimTasksMutation({
    refetchQueries: Number.isFinite(parsedWorkflowId)
      ? [{ query: GetWorkflowDocument, variables: { id: parsedWorkflowId } }]
      : [],
    awaitRefetchQueries: true,
    onCompleted: () => toast.success("Tasks assigned to you"),
    onError: (e) => toast.error(e.message),
  });

  const stepsForAssignCheck = useMemo(
    () => (detailedTaskSteps.length > 0 ? detailedTaskSteps : (workflow?.taskSteps ?? [])),
    [detailedTaskSteps, workflow],
  );

  // Button is only offered when every internal (non-external-approval) task is
  // still unassigned — matches the CRLT-640 gating and mirrors the "workflow
  // is fresh, claim it all for me" use case.
  const isBulkAssignable = useMemo(() => {
    const internalTasks = stepsForAssignCheck.filter(
      (t) => t.taskType !== TaskType.EXTERNAL_APPROVAL,
    );
    return internalTasks.length > 0 && internalTasks.every((t) => !t.assignee);
  }, [stepsForAssignCheck]);

  const handleBulkAssignToMe = useCallback(async () => {
    await bulkClaimTasks({ variables: { workflowId: parsedWorkflowId } });
  }, [bulkClaimTasks, parsedWorkflowId]);

  // Optimistic tracking: record which subtask IDs have been answered locally.
  // This lets the Complete button respond immediately to user interaction without
  // waiting for the server mutation + refetch round-trip. The backend FSM still
  // validates required subtasks on the resolve_task call.
  const [localValues, setLocalValues] = useState<Map<number, string | boolean>>(new Map());
  // Reset when the user navigates to a different task.
  const [lastSeenTaskId, setLastSeenTaskId] = useState<string | null>(activeTaskId);
  if (activeTaskId !== lastSeenTaskId) {
    setLastSeenTaskId(activeTaskId);
    setLocalValues(new Map());
  }

  const toggleSubtask = async (subtaskId: number, completed: boolean): Promise<void> => {
    setLocalValues((prev) => new Map(prev).set(subtaskId, completed));
    await _toggleSubtask(subtaskId, completed);
  };

  const updateSubtaskValue = async (
    subtaskId: number,
    value: string | boolean,
    currentlyCompleted: boolean,
  ): Promise<void> => {
    setLocalValues((prev) => new Map(prev).set(subtaskId, value));
    await _updateSubtaskValue(subtaskId, value, currentlyCompleted);
  };

  const commentsLoading = false;
  const comments: never[] = [];

  const activeTaskDetail = useMemo(() => {
    if (!activeTaskId) return null;
    return detailedTaskSteps.find((t) => String(t.id) === activeTaskId) ?? null;
  }, [activeTaskId, detailedTaskSteps]);

  // Deliverables shown to the GP in the active-task view. Restricted to
  // externally-visible documents — the isExternal flag is how the BE signals
  // a doc has been shared with the assigned GP.
  const taskDeliverables = useMemo(() => {
    if (!activeTaskDetail) return [];
    return documents.filter((d) => d.taskId === activeTaskDetail.id && d.isExternal);
  }, [documents, activeTaskDetail]);

  const nextTaskGroup = useMemo(() => {
    if (!activeTaskDetail) return undefined;
    const sorted = [...detailedTaskSteps].sort((a, b) => a.stepOrder - b.stepOrder);
    const currentIndex = sorted.findIndex((t) => t.id === activeTaskDetail.id);
    if (currentIndex < 0) return undefined;
    const next = sorted
      .slice(currentIndex + 1)
      .find((t) => t.taskType === TaskType.EXTERNAL_APPROVAL);
    if (!next) return undefined;
    return { taskId: String(next.id), taskLabel: next.name };
  }, [activeTaskDetail, detailedTaskSteps]);

  const allSubtasksCompleted = useMemo(() => {
    if (!activeTaskDetail) return false;
    // Mirror the backend FSM: only REQUIRED subtasks gate completion.
    // Optional subtasks (isRequired=false/undefined) must never block the button.
    const checkSubtasks = (subtasks: typeof activeTaskDetail.subtasks): boolean => {
      for (const s of subtasks) {
        // Document-upload subtasks never gate completion — the backend FSM
        // skips DOCUMENT_UPLOAD items too. A materialized child with a
        // null/unrecognized itemType is rendered as an upload (see
        // ChecklistItem), so apply the same coercion and skip it here.
        const isKnownType = Object.values(SubtaskItemType).includes(s.itemType);
        const effectiveType = isKnownType ? s.itemType : SubtaskItemType.DOCUMENT_UPLOAD;
        if (effectiveType === SubtaskItemType.DOCUMENT_UPLOAD) continue;

        const localVal = localValues.has(s.id) ? localValues.get(s.id) : s.value;
        // For radio groups, 'no' is a non-empty string and thus true-ish.
        // For checkboxes, false is an explicit un-tick, which means not completed.
        // isSubtaskValueComplete rejects whitespace-only strings and empty/all-
        // empty JSON arrays so INPUT_FIELDs / REPEATABLE_INPUTs filled with
        // only whitespace don't falsely unlock Submit.
        const isCompletedOptimistic = localValues.has(s.id)
          ? isSubtaskValueComplete(localValues.get(s.id) ?? null)
          : s.isCompleted === true || isSubtaskValueComplete(s.value ?? null);

        if (s.isRequired) {
          if (!isCompletedOptimistic) return false;
        }

        if (s.conditional_children) {
          const isCheckboxLike =
            s.itemType === SubtaskItemType.CHECKBOX ||
            s.itemType === SubtaskItemType.CHECKBOX_GROUP ||
            s.itemType === SubtaskItemType.CHECKBOX_WITH_INPUT ||
            s.itemType === SubtaskItemType.ATTESTATION;

          if (isCheckboxLike) {
            if (isCompletedOptimistic) {
              for (const children of Object.values(s.conditional_children)) {
                if (!checkSubtasks(children)) return false;
              }
            }
          } else {
            // For RadioGroup, the trigger is the string value.
            const activeTrigger = typeof localVal === "string" ? localVal : undefined;
            if (activeTrigger && s.conditional_children[activeTrigger]) {
              if (!checkSubtasks(s.conditional_children[activeTrigger])) return false;
            }
          }
        }
      }
      return true;
    };

    return checkSubtasks(activeTaskDetail.subtasks);
  }, [activeTaskDetail, localValues]);

  const handleActionClick = (action: string, payload?: Record<string, unknown>): void => {
    if (!activeTaskDetail) return;

    const doAction = () =>
      flushPendingAttachments()
        .then(() => performAction(activeTaskDetail.id, action, payload))
        .then(() => {
          if (TASK_CLOSE_ACTIONS.has(action)) {
            onTaskIdChange(null);
          }
        });

    // If the maker staged a new document for a DOCUMENT_UPLOAD subtask,
    // detach the previously-attached document(s) for this task before
    // flushing the new one. If nothing was staged, the existing document
    // stays — no detach needed.
    const isMakerSubmitAction = MAKER_SUBMISSION_ACTIONS.has(action);

    const hasStagedDocsForTask = stagedDocuments.some((d) => d.taskId === activeTaskDetail.id);

    const hasDocUploadSubtask = (subtasks: typeof activeTaskDetail.subtasks): boolean => {
      for (const s of subtasks) {
        if (s.itemType === SubtaskItemType.DOCUMENT_UPLOAD) return true;
        if (s.conditional_children) {
          for (const children of Object.values(s.conditional_children)) {
            if (hasDocUploadSubtask(children)) return true;
          }
        }
      }
      return false;
    };

    if (
      isMakerSubmitAction &&
      hasDocUploadSubtask(activeTaskDetail.subtasks) &&
      hasStagedDocsForTask
    ) {
      // Pass the staged doc IDs as the desired set so only previously-attached
      // docs that aren't being replaced get detached.
      const stagedIdsForTask = stagedDocuments
        .filter((d) => d.taskId === activeTaskDetail.id)
        .map((d) => d.documentId);
      void detachObsoleteDocuments(activeTaskDetail.id, stagedIdsForTask)
        .then(doAction)
        .catch(() => {});
    } else {
      void doAction().catch(() => {});
    }
  };

  // Left-zone assignment button dispatcher.
  //   "claim"    — fires the ClaimTask mutation.
  //   "unassign" — clears the current assignment via the CancelTaskAssignment mutation.
  const handleAssignmentAction = (action: "claim" | "unassign"): void => {
    if (!activeTaskDetail) return;
    void performAction(activeTaskDetail.id, action).catch(() => {});
  };

  if (!workflow) {
    return null;
  }

  const arenaId = workflow.clientId ? Number(workflow.clientId) : undefined;

  const renderTasksTab = () => {
    if (isReadOnly) {
      return <JiraTaskList parentIssueKey={workflow.externalRefId ?? ""} />;
    }
    if (activeTaskDetail) {
      if (isGPX) {
        return (
          <TaskDetailGPX
            task={activeTaskDetail}
            onBack={() => onTaskIdChange(null)}
            workflowUserId={effectiveWorkflowUserId}
            deliverables={taskDeliverables}
            onShowComment={handleShowComment}
          />
        );
      }
      return (
        <SubtaskActionsProvider
          value={{
            toggleSubtask,
            updateSubtaskValue,
            stageDocument: (file, purpose) =>
              stageDocument(file, {
                taskId: activeTaskDetail.id,
                purpose: purpose as TaskDocumentPurpose,
              }),
            deleteUploadedDocument,
            detachDocument: (documentId) =>
              detachObsoleteDocuments(activeTaskDetail.id, [
                ...documents
                  .filter(
                    (d) => d.taskId === activeTaskDetail.id && d.documentId !== documentId,
                  )
                  .map((d) => d.documentId),
              ]),
            taskDocuments: documents
              .filter((d) => d.taskId === activeTaskDetail.id)
              .map((d) => ({
                documentId: d.documentId,
                fileName: d.document?.fileName ?? "",
              })),
            stagedTaskDocuments: stagedDocuments
              .filter((d) => d.taskId === activeTaskDetail.id)
              .map((d) => ({
                documentId: d.documentId,
                fileName: d.fileName,
              })),
          }}
        >
          <TaskDetail
            task={activeTaskDetail}
            onBack={() => onTaskIdChange(null)}
            workflowUserId={effectiveWorkflowUserId}
            actionLoading={actionLoading}
          />
        </SubtaskActionsProvider>
      );
    }
    if (workflowLoading) {
      return (
        <Flex
          alignItems="center"
          justifyContent="center"
          className={styles.loadingContainer}
          data-testid="tasks-loading-indicator"
        >
          <LoadingIndicator size="medium" />
        </Flex>
      );
    }
    // Mounted whenever the arena is known and this is not a read-only view;
    // the banner's per-family (FA / IS) check decides whether to render.
    // workflow.currentAssignee only mirrors the FA-level assignee and can be
    // set while IS tasks are still unassigned, so it must not gate here.
    const banner =
      clientTeamAutoAssignEnabled && !isGPX && arenaId !== undefined ? (
        <UnassignedOwnersBanner
          workflowId={parsedWorkflowId}
          arenaId={arenaId}
          taskSteps={stepsForAssignCheck}
        />
      ) : undefined;

    const onBulkAssignToMe =
      !clientTeamAutoAssignEnabled && isBulkAssignable && !isGPX
        ? handleBulkAssignToMe
        : undefined;

    return (
      <TaskList
        workflow={workflow}
        taskSteps={stepsForAssignCheck}
        onTaskClick={(taskId) => onTaskIdChange(taskId)}
        banner={banner}
        onBulkAssignToMe={onBulkAssignToMe}
      />
    );
  };

  return (
    <SlideoutDrawerV2
      isOpen={!!workflow}
      onClose={onClose}
      isDismissable
      overlayStyle={OverlayStyle.Transparent}
    >
      <Flex
        alignItems="flex-start"
        justifyContent="center"
        className={styles.drawerBody}
        data-testid="workflow-detail-drawer"
      >
        <Flex flexDirection="column" className={styles.drawerContent}>
          <div className={styles.headerSection}>
            <WorkflowHeader workflow={workflow} onClose={onClose} isGPX={isGPX} />
          </div>

          <div className={styles.tabsContainer}>
            <Tabs>
              <Tab
                active={activeTab === DetailTabType.Tasks}
                text="Tasks"
                onClick={() => {
                  setActiveTab(DetailTabType.Tasks);
                  onTaskIdChange(null);
                  void refetchWorkflow();
                }}
                data-testid="detail-tasks-tab"
              />
              <Tab
                active={activeTab === DetailTabType.Communications}
                text="Communications"
                onClick={() => setActiveTab(DetailTabType.Communications)}
                data-testid="detail-communications-tab"
              />
              <Tab
                active={activeTab === DetailTabType.Documents}
                text="Documents"
                onClick={() => {
                  setActiveTab(DetailTabType.Documents);
                  void refetchDocuments();
                }}
                data-testid="detail-documents-tab"
              />
              {(isReadOnly || SHOW_IN_APP_REQUEST_DETAILS) && (
                <Tab
                  active={activeTab === DetailTabType.RequestDetails}
                  text="Request details"
                  onClick={() => setActiveTab(DetailTabType.RequestDetails)}
                  data-testid="detail-request-details-tab"
                />
              )}
            </Tabs>
          </div>
          <div className={styles.tabContent}>
            {actionLoading && (
              <Flex
                alignItems="center"
                justifyContent="center"
                className={`${styles.loadingContainer} ${styles.loadingOverlay}`}
                data-testid="task-action-loading-indicator"
              >
                <LoadingIndicator size="large" />
              </Flex>
            )}
            {activeTab === DetailTabType.Tasks && renderTasksTab()}
            {activeTab === DetailTabType.Communications &&
              (isReadOnly ? (
                <ClientCommunicationsTab
                  issueKey={workflow.externalRefId ?? ""}
                  jiraBaseURL={JIRA_BASE_URL}
                />
              ) : (
                <CommentsTab
                  issueKey={workflow.externalRefId ?? ""}
                  comments={comments}
                  loading={commentsLoading}
                  isGPX={isGPX}
                  targetCommentId={targetCommentId}
                  onTargetCommentConsumed={() => setTargetCommentId(null)}
                />
              ))}
            {activeTab === DetailTabType.Documents &&
              (isReadOnly ? (
                <div className={styles.taskListContainer}>
                  <EmptyStateCard
                    icon="file-alt"
                    iconVariant="solid"
                    title="No documents"
                    subtitle="Documents for this workflow are managed in Jira."
                    data-testid="jira-documents-empty"
                  />
                </div>
              ) : (
                <DocumentsTab
                  documents={documents}
                  stagedDocuments={stagedDocuments}
                  loading={docsLoading}
                />
              ))}
            {activeTab === DetailTabType.RequestDetails &&
              (isReadOnly ? (
                <RequestDetailsTab issueKey={workflow.externalRefId ?? ""} />
              ) : (
                <div className={styles.taskListContainer}>
                  <EmptyStateCard
                    icon="clock"
                    title="Coming soon"
                    subtitle="Request details for this workflow will be available here soon."
                    data-testid="request-details-coming-soon"
                  />
                </div>
              ))}
          </div>

          {!isReadOnly && activeTab === DetailTabType.Tasks && activeTaskDetail && (
            <ActionBar
              task={activeTaskDetail}
              actionLoading={actionLoading}
              allSubtasksCompleted={allSubtasksCompleted}
              isDocumentUploading={isUploadInProgress}
              onActionClick={handleActionClick}
              onAssignmentAction={handleAssignmentAction}
              isGPX={isGPX}
              workflowUserId={effectiveWorkflowUserId}
              arenaId={arenaId}
              issueKey={workflow.externalRefId ?? ""}
              stageDocument={stageDocument}
              deleteUploadedDocument={deleteUploadedDocument}
              nextTask={nextTaskGroup}
              clientTeamAutoAssignEnabled={clientTeamAutoAssignEnabled}
            />
          )}
        </Flex>
      </Flex>
    </SlideoutDrawerV2>
  );
};

export default WorkflowDetail;
